import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router-dom';
import { getAllUserTransactions } from 'service';
import { colorTheme } from 'theme';
import {
  commonConstantTexts,
  transactionOrderTypeOption,
  transactionTypes,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { CustomDataGrid } from 'components';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { dateFormat, UnitFormater, getSign } from 'utils/GeneralUtil';
import { useSelector } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { LoadingButton } from '@mui/lab';

export interface TransactionType {
  id: number;
  allocationAmount: string;
  date: string;
  fund: { symbol: string };
  unitsAllocated: string;
}

const useStyles = makeStyles({
  paperContainer: {
    width: '100%',
    padding: '10px',
    marginBottom: '20px',
  },
  emptyTransactionContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70vh',
    width: '100%',
  },
  emptyTransactionText: {
    fontFamily: 'Inter!important',
    color: '#838383',
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.2,
  },
});

const Transactions = (): JSX.Element => {
  const { id } = useParams();
  const classes = useStyles();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [orderType, setOrderType] = useState('');
  const pageInfo = useSelector((state) => state?.pagination?.allTransaction);

  const getTransactions = (): void => {
    if (id) {
      getAllUserTransactions(
        id,
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        searchValue,
        orderType,
      )
        .then(({ result }) => {
          setTransactions(result.rows);
          setPageCount(result?.count);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  useEffect(() => {
    getTransactions();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const getOrderType = (type: string): string => {
    switch (type) {
      case 'Buy':
        return strings.displayText.buy;
      case 'Sell':
        return strings.displayText.sell;
      case 'Fees':
        return strings.displayText.charges;
      case 'Re-Allocation':
        return strings.displayText.contributionAdjustment;
      case 'Contributions':
        return strings.displayText.contributionReceived;
      case 'Transfer-In':
        return strings.displayText.transferInReceived;
      default:
        return '-';
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      //   sortable: !id,
      width: 75,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          ID
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.id}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.allocatedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.filledDate || row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'orderType',
      sortable: false,
      width: 220,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.type}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getOrderType(row?.type)}
        </Typography>
      ),
    },
    {
      field: 'fund.description',
      sortable: !id,
      width: 450,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip
          title={
            row?.type === 'Fees' || row?.type === 'Re-Allocation'
              ? row?.fundShortNameForFees
              : row?.description
          }
          placement="top"
        >
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.type === 'Fees' || row?.type === 'Re-Allocation'
              ? row?.fundShortNameForFees
              : row?.shortDescription || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'amount',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountEuro}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const sign = getSign(row?.type, 'amount');
        const value =
          row?.type === transactionTypes.monthlyCharge
            ? 0
            : row?.type === 'Re-Allocation'
            ? row.amount
            : Math.abs(row?.amount);
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {`${sign} ${CurrencyFormater(value)}`}
          </Typography>
        );
      },
    },
    {
      field: 'units',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.units}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const sign =
          row?.type === 'Re-Allocation' ? '' : getSign(row?.type, 'units');

        const value =
          row?.type === 'Re-Allocation' ? row?.units : Math.abs(row?.units);
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {(row?.units && `${sign} ${UnitFormater(Number(value))}`) || '-'}
          </Typography>
        );
      },
    },

    {
      field: 'orderInfo.orderDate',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.type !== transactionTypes.monthlyCharge && row?.orderDate
            ? dateFormat(row?.orderDate)
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'orderInfo.settlementDate',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.settlementDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.settlementDate) || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.type === 'Contributions' ||
          row?.type === 'Fees' ||
          row?.type === 'Re-Allocation' ||
          row?.type === 'Transfer-In'
            ? `${strings.displayText.completedText}`
            : row?.orderStatus === 'Filled'
            ? `${strings.displayText.completedText}`
            : `${strings.displayText.inProgress}`}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {transactions.length === 0 ? (
        <Box className={classes.emptyTransactionContainer}>
          <Typography className={classes.emptyTransactionText}>
            {strings.displayText.transcationEmptyText}
          </Typography>
        </Box>
      ) : (
        <CustomDataGrid
          columns={columns}
          rows={transactions}
          getRowId={(row: Request) => `${String(row?.id)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? commonConstantTexts.evenText
              : commonConstantTexts.oddText
          }
          currentPage={commonConstantTexts.allTransaction}
          pageCount={pageCount}
          searchEnabled={true}
          handleSearch={handleSearch}
          searchPlaceholder={strings.displayText.searchLabel5}
          sortingMode={strings.displayText.sortingServer}
          rightContent={
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="order-type-label">
                  {strings.displayText.orderType}
                </InputLabel>
                <Select
                  labelId="order-type-label"
                  value={orderType}
                  label={strings.displayText.orderType}
                  onChange={(e) => {
                    setOrderType(e.target.value);
                  }}
                >
                  {transactionOrderTypeOption.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  getTransactions();
                }}
              >
                {strings.displayText.filter}
              </LoadingButton>
            </Box>
          }
        />
      )}
    </Box>
  );
};

export default Transactions;
